import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Chip,
  Tooltip,
  Divider,
  TextField,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import { useQuery, useMutation } from "react-query";
import { useSelector } from "react-redux";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import TeamMembersDialog from "./TeamMembersDialog";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

const ChatOrderSettingsDialog = ({
  open,
  onClose,
  onSave,
  locationId,
  locationName,
  locationData,
  currentSettings = {},
  onRefetch, // Add refetch callback prop
}) => {
  const { t } = useTranslation("dashboard");
  const selectedBusinessId = useSelector(
    (state) => state.auth.selectedBusinessId
  );
  const taxTypeOptions = useMemo(
    () => [
      { value: "custom", label: t("chatOrderSettingsTaxTypeCustom") },
      { value: "vat", label: t("chatOrderSettingsTaxTypeVat") },
      { value: "gst", label: t("chatOrderSettingsTaxTypeGst") },
      { value: "cgst", label: t("chatOrderSettingsTaxTypeCgst") },
      { value: "sgst", label: t("chatOrderSettingsTaxTypeSgst") },
      { value: "igst", label: t("chatOrderSettingsTaxTypeIgst") },
      { value: "sales tax", label: t("chatOrderSettingsTaxTypeSalesTax") },
      { value: "service tax", label: t("chatOrderSettingsTaxTypeServiceTax") },
      { value: "excise duty", label: t("chatOrderSettingsTaxTypeExciseDuty") },
      { value: "import duty", label: t("chatOrderSettingsTaxTypeImportDuty") },
      { value: "export duty", label: t("chatOrderSettingsTaxTypeExportDuty") },
      { value: "local tax", label: t("chatOrderSettingsTaxTypeLocalTax") },
      { value: "state tax", label: t("chatOrderSettingsTaxTypeStateTax") },
      { value: "federal tax", label: t("chatOrderSettingsTaxTypeFederalTax") },
    ],
    [t]
  );
  const flowTitleMap = useMemo(
    () => ({
      "new orders": t("chatOrderSettingsFlowNewOrders"),
      "new items": t("chatOrderSettingsFlowNewItems"),
      "confirmed items": t("chatOrderSettingsFlowConfirmedItems"),
      "items being prepared": t("chatOrderSettingsFlowItemsBeingPrepared"),
      "ready to serve": t("chatOrderSettingsFlowReadyToServe"),
      "ready to pick-up": t("chatOrderSettingsFlowReadyToPickup"),
      "ready to deliver": t("chatOrderSettingsFlowReadyToDeliver"),
      "closed order": t("chatOrderSettingsFlowClosedOrder"),
      "cancelled items": t("chatOrderSettingsFlowCancelledItems"),
      "cancelled orders": t("chatOrderSettingsFlowCancelledOrders"),
      "declined items": t("chatOrderSettingsFlowDeclinedItems"),
      "declined orders": t("chatOrderSettingsFlowDeclinedOrders"),
      "order closed - full refund": t("chatOrderSettingsFlowOrderClosedFullRefund"),
      "order closed - partial refund": t("chatOrderSettingsFlowOrderClosedPartialRefund"),
      "payment outstanding": t("chatOrderSettingsFlowPaymentOutstanding"),
      "payment collected in app/wallet": t("chatOrderSettingsFlowPaymentCollectedWallet"),
      "payment collected with cash": t("chatOrderSettingsFlowPaymentCollectedCash"),
      "payment collected via card": t("chatOrderSettingsFlowPaymentCollectedCard"),
    }),
    [t]
  );
  const getFlowTitle = (title, step) => flowTitleMap[title] || title || t("chatOrderSettingsStep", { count: step });

  // api mutation for saving chat order settings
  const { mutate: saveChatOrderSettings, isLoading: isSaving } = useMutation({
    mutationFn: async (payload) => {
      const res = await axiosInstance.patch(
        ApiEndPoints.TEAM_LINKING.update_order_flow(locationId),
        payload
      );
      return res.data;
    },
    onSuccess: (res) => {
      if (res.status === "success") {
        // toast.success("chat order settings updated  ");

        // call refetch to get updated data from the api
        // this ensures the ui shows the latest data after save
        if (onRefetch) {
          onRefetch();
        }

        // call onsave callback if provided
        onSave && onSave(res.data);

        onClose();
      } else {
        toast.error(res.message || t("chatOrderSettingsUpdateFailed"));
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("chatSettingsSomethingWentWrong"));
    },
  });

  // fetch team member data
  const { data: teamMemberList } = useQuery(
    ["teamMemberList", selectedBusinessId],
    async ({ signal }) => {
      const res = await axiosInstance.post(
        ApiEndPoints.TEAM_LINKING.get_team_member(selectedBusinessId),
        { 
          signal, 
          requestStatus: "1"
        }
      );
      return res.data.data;
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!selectedBusinessId,
    }
  );

  // state for tax settings - dynamic based on api data
  const [taxSettings, setTaxSettings] = useState([]);

  // state for payment settings
  const [paymentSettings, setPaymentSettings] = useState({
    tipField: "no",
    tipPercentage: [], // array of numbers for multiple tip percentages
    upfrontPayment: "yes",
    upfrontPercentage: "0%",
    inAppWallet: false,
    orderQueueLimitPerHour: 1000,
    orderIdPrefix: "",
  });

  // state for order flow settings - dynamic based on api data
  const [orderFlowSettings, setOrderFlowSettings] = useState([]);

  // state for team members dialog
  const [teamMembersDialogOpen, setTeamMembersDialogOpen] = useState(false);
  const [selectedFlowItem, setSelectedFlowItem] = useState(null);
  const [selectedFlowIndex, setSelectedFlowIndex] = useState(-1);

  // percentage options for tax and upfront (0% to 20%)
  const percentageOptions = Array.from({ length: 101 }, (_, i) => `${i}%`);
  
  // tip percentage options (1 to 100)
  const tipPercentageOptions = Array.from({ length: 100 }, (_, i) => i + 1);

  // initialize data when dialog opens
  useEffect(() => {
    if (open && locationData) {
      console.log("location data received:", locationData);

      // initialize order flow settings from api data
      if (locationData.orderFlow && Array.isArray(locationData.orderFlow)) {
        // use the existing orderflow data as-is, ensuring all required fields are present
        const formattedOrderFlow = locationData.orderFlow.map(
          (flowItem, index) => ({
            step: flowItem.step || index + 1,
            title: flowItem.title || `step ${index + 1}`,
            statusType: flowItem.statusType || "order", // preserve statusType from API
            description: flowItem.description || "",
            department: flowItem.department || [],
            teamMemberId: flowItem.teamMemberId || [],
          })
        );
        console.log("formatted order flow:", formattedOrderFlow);
        setOrderFlowSettings(formattedOrderFlow);
      } else {
        // set default order flow if none exists
        const defaultOrderFlow = [
          {
            step: 1,
            title: "new orders",
            statusType: "order",
            description: "",
            department: [],
            teamMemberId: [],
          },
          {
            step: 2,
            title: "new items",
            statusType: "item",
            description: "",
            department: [],
            teamMemberId: [],
          },
          {
            step: 3,
            title: "confirmed items",
            statusType: "item",
            description: "",
            department: [],
            teamMemberId: [],
          },
          {
            step: 4,
            title: "items being prepared",
            statusType: "item",
            description: "",
            department: [],
            teamMemberId: [],
          },
          {
            step: 5,
            title: "ready to serve",
            statusType: "item",
            description: "",
            department: [],
            teamMemberId: [],
          },
          {
            step: 6,
            title: "ready to pick-up",
            statusType: "item",
            description: "",
            department: [],
            teamMemberId: [],
          },
          {
            step: 7,
            title: "ready to deliver",
            statusType: "item",
            description: "",
            department: [],
            teamMemberId: [],
          },
          {
            step: 8,
            title: "closed order",
            statusType: "order",
            description: "",
            department: [],
            teamMemberId: [],
          },
          {
            step: 9,
            title: "cancelled items",
            statusType: "item",
            description: "",
            department: [],
            teamMemberId: [],
          },
          {
            step: 10,
            title: "cancelled orders",
            statusType: "order",
            description: "",
            department: [],
            teamMemberId: [],
          },
          {
            step: 11,
            title: "declined items",
            statusType: "item",
            description: "",
            department: [],
            teamMemberId: [],
          },
          {
            step: 12,
            title: "declined orders",
            statusType: "order",
            description: "",
            department: [],
            teamMemberId: [],
          },
          {
            step: 13,
            title: "order closed - full refund",
            statusType: "order",
            description: "",
            department: [],
            teamMemberId: [],
          },
          {
            step: 14,
            title: "order closed - partial refund",
            statusType: "order",
            description: "",
            department: [],
            teamMemberId: [],
          },
          {
            step: 15,
            title: "payment outstanding",
            statusType: "whole_order_payment",
            description: "",
            department: [],
            teamMemberId: [],
          },
          {
            step: 16,
            title: "payment collected in app/wallet",
            statusType: "whole_order_payment",
            description: "",
            department: [],
            teamMemberId: [],
          },
          {
            step: 17,
            title: "payment collected with cash",
            statusType: "whole_order_payment",
            description: "",
            department: [],
            teamMemberId: [],
          },
          {
            step: 18,
            title: "payment collected via card",
            statusType: "whole_order_payment",
            description: "",
            department: [],
            teamMemberId: [],
          },
        ];
        setOrderFlowSettings(defaultOrderFlow);
      }

      // Initialize tax settings from API data
      if (
        locationData.taxes &&
        Array.isArray(locationData.taxes) &&
        locationData.taxes.length > 0
      ) {
        // Convert API tax format to UI format
        // API structure: taxes: [{ countryCode, region, taxRates: [{ type, rate, amount }] }]
        const uiTaxSettings = locationData.taxes.flatMap((tax, taxIndex) =>
          tax.taxRates.map((taxRate, rateIndex) => ({
            id: `${taxIndex}_${rateIndex}`,
            enabled: true, // Always enabled - no checkbox needed
            type: taxRate.type,
            percentage: `${taxRate.rate}%`,
            countryCode: tax.countryCode,
            region: tax.region,
            amount: taxRate.amount || 0,
          }))
        );
        console.log("Converted tax settings:", uiTaxSettings);
        setTaxSettings(uiTaxSettings);
      } else {
        // Create default tax structure if no taxes exist
        const defaultTaxes = [
          {
            id: 1,
            enabled: true,
            type: "custom",
            percentage: "0%",
            countryCode: "IND",
            region: "GUJARAT",
            amount: 0,
          },
          {
            id: 2,
            enabled: true,
            type: "custom",
            percentage: "0%",
            countryCode: "IND",
            region: "GUJARAT",
            amount: 0,
          },
          {
            id: 3,
            enabled: true,
            type: "custom",
            percentage: "0%",
            countryCode: "IND",
            region: "GUJARAT",
            amount: 0,
          },
        ];
        setTaxSettings(defaultTaxes);
      }

      // Initialize payment settings from API data
      if (locationData.paymentSettings) {
        // Handle tipPercentage as array or single value
        let tipPercentageArray = [];
        if (locationData.paymentSettings.tipPercentage) {
          if (Array.isArray(locationData.paymentSettings.tipPercentage)) {
            tipPercentageArray = locationData.paymentSettings.tipPercentage;
          } else {
            // Convert single value to array for backward compatibility
            tipPercentageArray = [locationData.paymentSettings.tipPercentage];
          }
        }
        
        setPaymentSettings({
          tipField: locationData.paymentSettings.enableTipField ? "yes" : "no",
          tipPercentage: tipPercentageArray,
          upfrontPayment: locationData.paymentSettings.autoCollectUpfront
            ? "yes"
            : "no",
          upfrontPercentage: `${
            locationData.paymentSettings.upfrontPercentage || 0
          }%`,
          inAppWallet:
            locationData.paymentSettings.enableInAppWalletPayments || false,
          orderQueueLimitPerHour: locationData.paymentSettings.orderQueueLimitPerHour ?? 1000,
          orderIdPrefix: locationData.paymentSettings.orderIdPrefix ?? "",
        });
      } else {
        // Set default payment settings
        setPaymentSettings({
          tipField: "no",
          tipPercentage: [],
          upfrontPayment: "yes",
          upfrontPercentage: "0%",
          inAppWallet: false,
          orderQueueLimitPerHour: 1000,
          orderIdPrefix: "",
        });
      }
    }
  }, [open, locationData]);

  // Handle tax setting changes (only type and percentage can be changed)
  const handleTaxChange = (taxIndex, field, value) => {
    setTaxSettings((prev) => {
      const updated = [...prev];
      if (updated[taxIndex]) {
        updated[taxIndex] = {
          ...updated[taxIndex],
          [field]: value,
        };
      }
      return updated;
    });
  };

  // Handle payment setting changes
  const handlePaymentChange = (field, value) => {
    setPaymentSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };



  // Handle order flow setting changes
  const handleOrderFlowChange = (statusKey, field, value) => {
    setOrderFlowSettings((prev) => ({
      ...prev,
      [statusKey]: {
        ...prev[statusKey],
        [field]: value,
      },
    }));
  };

  // Handle edit team members for order flow item
  const handleEditTeamMembers = (flowItem, index) => {
    // Format the flow item as a category for the TeamMembersDialog
    const categoryForDialog = {
      name: flowItem.title || `Step ${flowItem.step}`,
      department: flowItem.department || [],
      teamMemberId: flowItem.teamMemberId || [],
    };

    setSelectedFlowItem(categoryForDialog);
    setSelectedFlowIndex(index);
    setTeamMembersDialogOpen(true);
  };

  // Handle save team members changes
  const handleSaveTeamMembers = (updatedCategories) => {
    console.log(
      "Team members updated, received categories:",
      updatedCategories
    );

    // The TeamMembersDialog returns updated categories, but we need to update our order flow settings
    // Since we're passing only one category, it will be at index 0
    const updatedCategory = updatedCategories[0];
    console.log("Updated category:", updatedCategory);
    console.log("Selected flow index:", selectedFlowIndex);

    if (updatedCategory && selectedFlowIndex >= 0) {
      setOrderFlowSettings((prev) => {
        const updated = [...prev];
        if (updated[selectedFlowIndex]) {
          updated[selectedFlowIndex] = {
            ...updated[selectedFlowIndex],
            department: updatedCategory.department || [],
            teamMemberId: updatedCategory.teamMemberId || [],
          };
          console.log("Updated order flow item:", updated[selectedFlowIndex]);
        }
        return updated;
      });
    }

    setTeamMembersDialogOpen(false);
    setSelectedFlowItem(null);
    setSelectedFlowIndex(-1);
  };

  // Handle save
  const handleSave = () => {
    // Validate locationId is provided
    if (!locationId) {
      toast.error(t("chatOrderSettingsLocationRequired"));
      return;
    }

    // Format tax settings according to the API structure
    // All taxes are automatically enabled - no need to filter
    const allTaxes = taxSettings;

    // Group all taxes by countryCode and region
    const taxGroups = {};
    allTaxes.forEach((tax) => {
      const key = `${tax.countryCode}_${tax.region}`;
      if (!taxGroups[key]) {
        taxGroups[key] = {
          countryCode: tax.countryCode,
          region: tax.region,
          taxRates: [],
        };
      }

      const percentage = tax.percentage.replace("%", "");
      const rate = parseFloat(percentage);

      if (isNaN(rate) || rate < 0 || rate > 100) {
        toast.error(t("chatOrderSettingsInvalidTaxPercentage", { value: tax.percentage }));
        return;
      }

      taxGroups[key].taxRates.push({
        type: tax.type,
        rate: rate,
        amount: tax.amount || 0,
      });
    });

    const formattedTaxes = Object.values(taxGroups);

    // Format payment settings according to the API structure
    const formattedPaymentSettings = {
      enableTipField: paymentSettings.tipField === "yes",
      // tipPercentage must always be an array - empty array when tip field is disabled
      tipPercentage: paymentSettings.tipField === "yes" 
        ? paymentSettings.tipPercentage.sort((a, b) => a - b) // sort array in ascending order
        : [],
      autoCollectUpfront: paymentSettings.upfrontPayment === "yes",
      upfrontPercentage:
        parseFloat(paymentSettings.upfrontPercentage.replace("%", "")) || 0,
      enableInAppWalletPayments: paymentSettings.inAppWallet,
      orderQueueLimitPerHour: paymentSettings.orderQueueLimitPerHour,
      orderIdPrefix: paymentSettings.orderIdPrefix,
    };

    // Format order flow settings - keep the existing structure
    const formattedOrderFlow = orderFlowSettings.map((flowItem) => ({
      step: flowItem.step,
      title: flowItem.title,
      statusType: flowItem.statusType || "order", // include statusType when saving
      description: flowItem.description || "",
      department: flowItem.department || [],
      teamMemberId: flowItem.teamMemberId || [],
    }));

    // Create the final payload according to the API structure
    const payload = {
      taxes: formattedTaxes,
      paymentSettings: formattedPaymentSettings,
      orderFlow: formattedOrderFlow,
    };

    console.log("Saving chat order settings:", payload);

    // Call the API mutation
    saveChatOrderSettings(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          color: "#7c28d6",
          fontWeight: 700,
          borderBottom: "1px solid #e0e0e0",
          pb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">{t("chatSettingsOrderSettings")}</Typography>
        <IconButton onClick={onClose} sx={{ color: "#666" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Selected Location Display */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ color: "#7c28d6", fontWeight: 600, mb: 1 }}>
            {t("chatOrderSettingsSelectedLocation")}
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: "#f7f2fa",
              borderRadius: 2,
              border: "1px solid #e6d0fa",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography sx={{ color: "#7c28d6", fontWeight: 500 }}>
              {locationName || t("chatOrderSettingsNoLocationSelected")}
            </Typography>
            {locationData?.isPrimaryAddress && (
              <Chip
                size="small"
                label={t("primaryLabel")}
                sx={{ height: 20, fontSize: "0.7em" }}
                color="primary"
              />
            )}
          </Box>
        </Box>

        {/* Tax Settings */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
            <Typography
              sx={{ color: "#7c28d6", fontWeight: 700, fontSize: "1.1em" }}
            >
              {t("chatOrderSettingsTaxSettings")}
            </Typography>
            <Tooltip
              title={t("chatOrderSettingsTaxTooltip")}
              arrow
              placement="top"
              enterDelay={300}
              leaveDelay={200}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 18,
                  color: "#666",
                  cursor: "help",
                  "&:hover": {
                    color: "#7c28d6",
                    transform: "scale(1.1)",
                    transition: "all 0.2s ease-in-out",
                  },
                }}
              />
            </Tooltip>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: 2,
            }}
          >
            {taxSettings.map((tax, index) => (
              <Box
                key={tax.id || index}
                sx={{
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  position: "relative",
                }}
              >
                {/* remove tax button */}
                {taxSettings.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() =>
                      setTaxSettings((prev) =>
                        prev.filter((_, i) => i !== index)
                      )
                    }
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      color: "#ff4444",
                      "&:hover": { bgcolor: "#ffebee" },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                )}

                {/* tax label - no checkbox needed */}
                <Typography sx={{ color: "#333", fontWeight: 600, mb: 1 }}>
                  {t("chatOrderSettingsTaxNumber", { count: index + 1 })}
                </Typography>

                {/* country and region display */}
                <Box
                  sx={{
                    mb: 1,
                    p: 1,
                    bgcolor: "#f5f5f5",
                    borderRadius: 1,
                    fontSize: "0.8em",
                  }}
                >
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    {t("chatOrderSettingsCountryRegion", {
                      country: tax.countryCode,
                      region: tax.region,
                    })}
                  </Typography>
                  {tax.amount > 0 && (
                    <Typography
                      variant="caption"
                      sx={{ color: "#666", display: "block", mt: 0.5 }}
                    >
                      {t("chatOrderSettingsAmount", { amount: tax.amount })}
                    </Typography>
                  )}
                </Box>

                <Stack gap={1}>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={tax.type || "custom"}
                      onChange={(e) =>
                        handleTaxChange(index, "type", e.target.value)
                      }
                      displayEmpty
                      sx={{ fontSize: "0.9em" }}
                    >
                      {taxTypeOptions.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={tax.percentage || "0%"}
                      onChange={(e) =>
                        handleTaxChange(index, "percentage", e.target.value)
                      }
                      displayEmpty
                      sx={{ fontSize: "0.9em" }}
                    >
                      {percentageOptions.map((percentage) => (
                        <MenuItem key={percentage} value={percentage}>
                          {percentage}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Box>
            ))}
          </Box>

          {/* add tax button */}
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Button
              variant="outlined"
              onClick={() => {
                const newTax = {
                  id: Date.now(),
                  enabled: true, // always enabled - no checkbox needed
                  type: "custom",
                  percentage: "0%",
                  countryCode: "IND",
                  region: "GUJARAT",
                  amount: 0,
                };
                setTaxSettings((prev) => [...prev, newTax]);
              }}
              sx={{
                borderColor: "#7c28d6",
                color: "#FFF",
                "&:hover": { borderColor: "#5a1b8a" },
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              {t("chatOrderSettingsAddTaxField")}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* payment settings */}
        <Box sx={{ mb: 3 }}>
          {/* <Typography
            sx={{ color: "#7c28d6", fontWeight: 700, fontSize: "1.1em", mb: 2 }}
          >
            payment settings
          </Typography> */}

          {/* tip field */}
          <Box sx={{ mb: 2 }}>
              <Typography sx={{ color: "#7c28d6", fontWeight: 600, mb: 1 }}>
              {t("chatOrderSettingsTipField")}
            </Typography>
            <RadioGroup
              row
              value={paymentSettings.tipField}
              onChange={(e) => handlePaymentChange("tipField", e.target.value)}
            >
              <FormControlLabel
                value="yes"
                control={<Radio sx={{ color: "#7c28d6" }} />}
                label={t("yes")}
              />
              <FormControlLabel
                value="no"
                control={<Radio sx={{ color: "#7c28d6" }} />}
                label={t("no")}
              />
            </RadioGroup>
            {paymentSettings.tipField === "yes" && (
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ color: "#666", fontSize: "0.9em", mb: 0.5 }}>
                  {t("chatOrderSettingsTipPercentages")}
                </Typography>
                <Autocomplete
                  multiple
                  options={tipPercentageOptions}
                  getOptionLabel={(option) => `${option}%`}
                  value={paymentSettings.tipPercentage}
                  onChange={(_, newValue) => {
                    handlePaymentChange("tipPercentage", newValue || []);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t("chatOrderSettingsTipPercentagesSelected", {
                        count: paymentSettings.tipPercentage.length,
                      })}
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: "white",
                        },
                      }}
                    />
                  )}
                  renderTags={() => null}
                  clearOnBlur
                  openOnFocus
                  disableClearable={false}
                  isOptionEqualToValue={(option, value) => option === value}
                  ListboxProps={{
                    style: {
                      maxHeight: 300,
                    },
                  }}
                />
                
                {/* Selected Tip Percentage Chips */}
                {paymentSettings.tipPercentage.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                    >
                      {paymentSettings.tipPercentage
                        .sort((a, b) => a - b)
                        .map((percentage) => (
                          <Chip
                            key={percentage}
                            label={`${percentage}%`}
                            size="small"
                            onDelete={() => {
                              const newSelection = paymentSettings.tipPercentage.filter(
                                (p) => p !== percentage
                              );
                              handlePaymentChange("tipPercentage", newSelection);
                            }}
                            sx={{
                              bgcolor: "#F1E4FD",
                              color: "#8800F7",
                              fontSize: "12px",
                              height: 24,
                              fontWeight: 500,
                              "& .MuiChip-deleteIcon": {
                                fontSize: 16,
                                color: "#8800F7",
                              },
                            }}
                          />
                        ))}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#666",
                        fontSize: "12px",
                        mt: 1,
                        display: "block",
                      }}
                    >
                      {t("selectedCount", { count: paymentSettings.tipPercentage.length })}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* upfront payment */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ color: "#7c28d6", fontWeight: 600, mb: 1 }}>
              {t("chatOrderSettingsUpfrontPayment")}
            </Typography>
            <RadioGroup
              row
              value={paymentSettings.upfrontPayment}
              onChange={(e) => {
                const newValue = e.target.value;
                handlePaymentChange("upfrontPayment", newValue);
                // If user selects "no", automatically set upfront percentage to 0%
                if (newValue === "no") {
                  handlePaymentChange("upfrontPercentage", "0%");
                }
              }}
            >
              <FormControlLabel
                value="yes"
                control={<Radio sx={{ color: "#7c28d6" }} />}
                label={t("yes")}
              />
              <FormControlLabel
                value="no"
                control={<Radio sx={{ color: "#7c28d6" }} />}
                label={t("no")}
              />
            </RadioGroup>
            {paymentSettings.upfrontPayment === "yes" && (
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ color: "#666", fontSize: "0.9em", mb: 0.5 }}>
                  {t("chatOrderSettingsUpfrontPercentage")}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={paymentSettings.upfrontPercentage}
                    onChange={(e) =>
                      handlePaymentChange("upfrontPercentage", e.target.value)
                    }
                    displayEmpty
                    sx={{ fontSize: "0.9em" }}
                  >
                    {percentageOptions.map((percentage) => (
                      <MenuItem key={percentage} value={percentage}>
                        {percentage}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>

          {/* additional order settings */}
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
              <Typography sx={{ color: "#7c28d6", fontWeight: 600 }}>
                {t("chatOrderSettingsInAppWallet")}
              </Typography>
              <Tooltip title={t("chatOrderSettingsInAppWalletTooltip")} arrow>
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </Tooltip>
            </Stack>
            <RadioGroup
              row
              value={paymentSettings.inAppWallet ? "yes" : "no"}
              onChange={(e) =>
                handlePaymentChange("inAppWallet", e.target.value === "yes")
              }
            >
              <FormControlLabel
                value="yes"
                control={<Radio sx={{ color: "#7c28d6" }} />}
                label={t("yes")}
              />
              <FormControlLabel
                value="no"
                control={<Radio sx={{ color: "#7c28d6" }} />}
                label={t("no")}
              />
            </RadioGroup>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
              <Typography sx={{ color: "#7c28d6", fontWeight: 600 }}>
                {t("chatOrderSettingsQueueLimit")}
              </Typography>
              <Tooltip title={t("chatOrderSettingsQueueLimitTooltip")} arrow>
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </Tooltip>
            </Stack>
            <TextField
              type="number"
              value={paymentSettings.orderQueueLimitPerHour}
              onChange={(e) => {
                const newValue = parseInt(e.target.value) || 0;
                handlePaymentChange("orderQueueLimitPerHour", newValue);
              }}
              placeholder={t("chatOrderSettingsQueueLimitPlaceholder")}
              size="small"
              sx={{
                maxWidth: 200,
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#f7f2fa",
                  borderRadius: 2,
                  fontSize: "0.9em",
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
              <Typography sx={{ color: "#7c28d6", fontWeight: 600 }}>
                {t("chatOrderSettingsOrderIdPrefix")}
              </Typography>
              <Tooltip title={t("chatOrderSettingsOrderIdPrefixTooltip")} arrow>
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </Tooltip>
            </Stack>
            <TextField
              value={paymentSettings.orderIdPrefix}
              onChange={(e) => {
                const newValue = e.target.value;
                handlePaymentChange("orderIdPrefix", newValue);
              }}
              placeholder={t("chatOrderSettingsOrderIdPrefixPlaceholder")}
              size="small"
              sx={{
                maxWidth: 200,
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#f7f2fa",
                  borderRadius: 2,
                  fontSize: "0.9em",
                },
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* order flow settings */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
            <Typography
              sx={{ color: "#7c28d6", fontWeight: 700, fontSize: "1.1em" }}
            >
              {t("chatOrderSettingsOrderFlow")}
            </Typography>
            <Tooltip
              title={t("chatOrderSettingsOrderFlowTooltip")}
              arrow
              placement="top"
              enterDelay={300}
              leaveDelay={200}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 18,
                  color: "#666",
                  cursor: "help",
                  "&:hover": {
                    color: "#7c28d6",
                    transform: "scale(1.1)",
                    transition: "all 0.2s ease-in-out",
                  },
                }}
              />
            </Tooltip>
          </Stack>

          {/* order flow tabs */}
          <Box sx={{ mb: 2 }}>
            <Chip
              label={t("chatOrderSettingsOrderItemStat")}
              sx={{
                bgcolor: "#7c28d6",
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.9em",
              }}
            />
          </Box>

          {/* order status list */}
          <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
            {console.log("rendering order flow items:", orderFlowSettings)}
            {orderFlowSettings.map((flowItem, index) => (
              <Box
                key={flowItem.step || index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  mb: 1,
                  bgcolor: "#fff",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#333",
                      textTransform: "lowercase",
                    }}
                  >
                    {getFlowTitle(flowItem.title, flowItem.step)}
                  </Typography>
                  {flowItem.description && (
                    <Typography
                      variant="caption"
                      sx={{ color: "#666", display: "block", mt: 0.5 }}
                    >
                      {flowItem.description}
                    </Typography>
                  )}
                </Box>

                <Stack direction="row" alignItems="center" gap={2}>
                  {/* department count */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 22,
                        bgcolor:
                          flowItem.department?.length > 0
                            ? "#e0eafc"
                            : "#f5f5f5",
                        color:
                          flowItem.department?.length > 0 ? "#2b63e3" : "#999",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.8em",
                        border:
                          flowItem.department?.length > 0
                            ? "1px solid #2b63e3"
                            : "1px solid #ddd",
                      }}
                    >
                      🏢
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          flowItem.department?.length > 0 ? "#2b63e3" : "#999",
                        fontWeight: flowItem.department?.length > 0 ? 600 : 400,
                      }}
                    >
                      ({flowItem.department?.length || 0})
                    </Typography>
                  </Box>

                  {/* team members count */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 22,
                        bgcolor:
                          flowItem.teamMemberId?.length > 0
                            ? "#eaf7fa"
                            : "#f5f5f5",
                        color:
                          flowItem.teamMemberId?.length > 0
                            ? "#135b76"
                            : "#999",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.8em",
                        border:
                          flowItem.teamMemberId?.length > 0
                            ? "1px solid #135b76"
                            : "1px solid #ddd",
                      }}
                    >
                      👥
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          flowItem.teamMemberId?.length > 0
                            ? "#135b76"
                            : "#999",
                        fontWeight:
                          flowItem.teamMemberId?.length > 0 ? 600 : 400,
                      }}
                    >
                      ({flowItem.teamMemberId?.length || 0})
                    </Typography>
                  </Box>

                  {/* edit team members button */}
                  <IconButton
                    size="small"
                    onClick={() => handleEditTeamMembers(flowItem, index)}
                    sx={{
                      p: 0.5,
                      color: "#7c28d6",
                      "&:hover": { bgcolor: "#f3e5f5" },
                      ml: 0.5,
                    }}
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Stack>
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>

      {/* team members dialog */}
      <TeamMembersDialog
        open={teamMembersDialogOpen}
        onClose={() => setTeamMembersDialogOpen(false)}
        category={selectedFlowItem}
        teamMemberList={teamMemberList}
        onSave={handleSaveTeamMembers}
        chatCategories={[selectedFlowItem].filter(Boolean)}
        categoryIndex={0}
        selectedLocationId={locationId}
      />

      <DialogActions sx={{ p: 3, pt: 2 }}>
      
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          sx={{
            bgcolor: "#7c28d6",
            "&:hover": { bgcolor: "#5a1b8a" },
            textTransform: "none",
            fontWeight: 500,
            px: 3,
          }}
        >
          {isSaving ? t("chatOrderSettingsSavingRefreshing") : t("saveChanges")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChatOrderSettingsDialog;
